import { EventViewerState, EventViewingState } from '../models/event-viewing.model';

export { EventViewerState } from '../models/event-viewing.model';

export type EventCta =
  | 'RSVP'
  | 'GOING'
  | 'WATCH'
  | 'JOIN'
  | 'SIGN_IN_TO_RSVP'
  | 'SIGN_IN_TO_WATCH'
  | 'ENDED'
  | 'UNAVAILABLE'
  | 'FULL'
  | 'REQUIREMENT';

export type EventRsvpResumeAction = 'SUBMIT' | 'CLEAR' | 'WAIT';

export function isEventLiveStatus(status?: string | null): boolean {
  return ['live', 'start', 'started'].includes((status || '').trim().toLowerCase());
}

export function terminalEventCta(
  status?: string | null,
  state?: EventViewingState | null
): 'ENDED' | 'UNAVAILABLE' | null {
  const normalized = (status || '').trim().toLowerCase();
  if (['closed', 'ended', 'end', 'stop', 'stopped'].includes(normalized) || state?.watchReason === 'EVENT_ENDED') return 'ENDED';
  if (['cancelled', 'canceled', 'unpublished'].includes(normalized) ||
      ['EVENT_UNAVAILABLE', 'EVENT_NOT_FOUND'].includes(state?.watchReason || '')) return 'UNAVAILABLE';
  return null;
}

export function isUpcomingRsvp(
  event: { isPublish?: boolean; isRsvp?: boolean; eventStatus?: string | null; viewingState?: EventViewingState | null },
  isOwner: boolean
): boolean {
  return event.isPublish === true && event.isRsvp === true && !isOwner &&
    !isEventLiveStatus(event.eventStatus) && !terminalEventCta(event.eventStatus, event.viewingState);
}

export function isEventOwnerForViewer(
  event: { byUserId?: number | null; byProfileId?: number | null },
  viewer?: { userId?: number | null; defaultProfileId?: number | null; reviewProfileId?: number | null } | null
): boolean {
  if (!viewer) return false;
  return Boolean(
    (viewer.userId && event.byUserId === viewer.userId) ||
    (viewer.defaultProfileId && event.byProfileId === viewer.defaultProfileId) ||
    (viewer.reviewProfileId && event.byProfileId === viewer.reviewProfileId)
  );
}

const RSVP_REASON_MESSAGES: Record<string, string> = {
  EVENT_NOT_FOUND: 'This event is no longer available.',
  EVENT_UNAVAILABLE: 'This event is unavailable.',
  EVENT_ENDED: 'This event has ended.',
  EVENT_FULL: 'This event is full.',
  RSVP_CLOSED: 'RSVP is closed for this event.',
  RSVP_DISABLED: 'RSVP is not available for this event.',
  RSVP_REQUIRED: 'RSVP to this event before adding it to your calendar.',
  EVENT_HOST: 'Hosts manage their event from the event details page.',
  ACCOUNT_NOT_ACTIVE: 'Activate your account before RSVPing.',
  AUTHENTICATION_REQUIRED: 'Sign in to continue.',
  EVENT_NOT_LIVE: 'This event is not live yet.',
  VERIFICATION_REQUIRED: 'You can watch this event. Chat is available after identity verification.',
  PUBLIC_IDENTITY_REQUIRED: 'You can watch this event. Set a verified public identity to use chat.',
  CHAT_DISABLED: 'You can watch this event, but attendee chat is disabled.',
  OUTSIDE_JURISDICTION: 'This event is limited to the host jurisdiction.',
};

export function selectEventCta(status: string | null | undefined, state: EventViewerState, signedIn: boolean): EventCta {
  const terminalCta = terminalEventCta(status, state);
  if (terminalCta) return terminalCta;
  // Do not trust a stale anonymous response that still advertises the retired
  // stream-only guest mode. Live event access always starts with sign-in.
  if (!signedIn && isEventLiveStatus(status)) return 'SIGN_IN_TO_WATCH';
  if (signedIn && state.watchReason === 'EVENT_FULL' && !state.canWatch) return 'FULL';
  if (state.canWatch) return state.watchMode === 'INTERACTIVE' ? 'JOIN' : 'WATCH';
  // Watching starts only once the event is live; RSVP does not. An upcoming
  // event can therefore have EVENT_NOT_LIVE while still accepting an eligible
  // signed-in attendee's RSVP.
  if (state.isRsvp) return 'GOING';
  if (signedIn && state.canRsvp) return 'RSVP';
  if (!signedIn &&
      (isTerminalRsvpReason(state.rsvpReason) || isTerminalRsvpReason(state.watchReason))) {
    return 'REQUIREMENT';
  }
  if (signedIn && state.watchReason) return 'REQUIREMENT';
  if (!signedIn && state.watchReason === 'AUTHENTICATION_REQUIRED') {
    return 'SIGN_IN_TO_RSVP';
  }
  if (signedIn && state.rsvpReason) return 'REQUIREMENT';
  return signedIn ? 'RSVP' : 'SIGN_IN_TO_RSVP';
}

export function isTerminalRsvpReason(reason?: string | null): boolean {
  return [
    'EVENT_NOT_FOUND',
    'EVENT_UNAVAILABLE',
    'EVENT_ENDED',
    'RSVP_CLOSED',
    'RSVP_DISABLED',
    'EVENT_HOST',
    'ACCOUNT_NOT_ACTIVE',
    'OUTSIDE_JURISDICTION',
  ].includes(reason || '');
}

export function selectEventRsvpResumeAction(state: EventViewerState): EventRsvpResumeAction {
  if (state.isRsvp) return 'CLEAR';
  if (state.canRsvp) return 'SUBMIT';
  if (isTerminalRsvpReason(state.rsvpReason) || isTerminalRsvpReason(state.watchReason)) return 'CLEAR';
  return 'WAIT';
}

export function rsvpReasonMessage(reason?: string | null): string {
  return reason && RSVP_REASON_MESSAGES[reason]
    ? RSVP_REASON_MESSAGES[reason]
    : 'Unable to update your RSVP. Please try again.';
}

export function calendarReasonMessage(reason?: string | null): string {
  if (reason === 'AUTHENTICATION_REQUIRED') {
    return 'Sign in to add this event to your calendar.';
  }
  if (reason === 'EVENT_NOT_FOUND' || reason === 'EVENT_UNAVAILABLE') {
    return 'This event is no longer available.';
  }
  return reason && RSVP_REASON_MESSAGES[reason]
    ? RSVP_REASON_MESSAGES[reason]
    : 'Unable to prepare this calendar event. Please try again.';
}
